import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MapPin, Plus, Trash, Pencil, User, Users, Crown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Pracas() {
  const { user } = useAuth();
  const [pracas, setPracas] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPraca, setEditingPraca] = useState(null);
  const [name, setName] = useState("");
  const [leaderId, setLeaderId] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [pracasRes, membersRes] = await Promise.all([
        axios.get(`${API}/pracas`),
        axios.get(`${API}/team/members`).catch(() => ({ data: [] })),
      ]);
      setPracas(pracasRes.data);
      setMembers(membersRes.data || []);
    } catch { toast.error("Erro ao carregar praças"); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Nome é obrigatório"); return; }
    try {
      if (editingPraca) {
        await axios.put(`${API}/pracas/${editingPraca.id}`, {
          name, leader_id: leaderId || null, member_ids: selectedMembers,
        });
        toast.success("Praça atualizada!");
      } else {
        await axios.post(`${API}/pracas`, {
          name, leader_id: leaderId || null, member_ids: selectedMembers,
        });
        toast.success("Praça criada!");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
  };

  const handleEdit = (praca) => {
    setEditingPraca(praca);
    setName(praca.name);
    setLeaderId(praca.leader_id || "");
    setSelectedMembers(praca.member_ids || []);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remover esta praça?")) return;
    try {
      await axios.delete(`${API}/pracas/${id}`);
      toast.success("Praça removida");
      fetchData();
    } catch { toast.error("Erro ao remover"); }
  };

  const resetForm = () => {
    setEditingPraca(null);
    setName("");
    setLeaderId("");
    setSelectedMembers([]);
  };

  const getMemberName = (id) => {
    const m = members.find(m => m.id === id || m._id === id);
    return m?.name || m?.email || id;
  };

  const toggleMember = (id) => {
    setSelectedMembers(prev => prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-900" /></div>;

  return (
    <div className="space-y-6" data-testid="pracas-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-zinc-950 flex items-center gap-2">
            <MapPin size={24} weight="bold" /> Praças
          </h2>
          <p className="text-sm text-zinc-500 mt-1">Organize sua cozinha em estações de trabalho</p>
        </div>
        <Button size="sm" onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-zinc-950 text-white hover:bg-zinc-800" data-testid="new-praca-btn">
          <Plus size={16} className="mr-1.5" /> Nova Praça
        </Button>
      </div>

      {pracas.length === 0 ? (
        <Card className="border-dashed border-zinc-300">
          <CardContent className="py-12 text-center">
            <MapPin size={40} className="mx-auto text-zinc-300 mb-3" />
            <p className="text-zinc-500 font-medium">Nenhuma praça criada</p>
            <p className="text-xs text-zinc-400 mt-1">Crie praças como Confeitaria, Linha Quente, Garde Manger</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {pracas.map(praca => (
            <Card key={praca.id} className="border-zinc-200 hover:border-zinc-300 transition-colors" data-testid={`praca-${praca.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                      <MapPin size={20} className="text-violet-700" weight="fill" />
                    </div>
                    <div>
                      <p className="font-bold text-zinc-900">{praca.name}</p>
                      <p className="text-[10px] text-zinc-400">{(praca.member_ids || []).length + (praca.leader_id ? 1 : 0)} membro(s)</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-zinc-400 hover:text-zinc-700" onClick={() => handleEdit(praca)} data-testid={`edit-praca-${praca.id}`}>
                      <Pencil size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-zinc-400 hover:text-red-500" onClick={() => handleDelete(praca.id)} data-testid={`delete-praca-${praca.id}`}>
                      <Trash size={14} />
                    </Button>
                  </div>
                </div>
                
                {praca.leader_id && (
                  <div className="mt-3 flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 rounded-lg px-2 py-1.5">
                    <Crown size={12} weight="fill" />
                    <span className="font-semibold">Líder:</span> {getMemberName(praca.leader_id)}
                  </div>
                )}
                
                {(praca.member_ids || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {praca.member_ids.map(mid => (
                      <span key={mid} className="text-[10px] bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <User size={10} /> {getMemberName(mid)}
                      </span>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MapPin size={20} /> {editingPraca ? "Editar Praça" : "Nova Praça"}</DialogTitle>
            <DialogDescription className="sr-only">Criar ou editar praça de trabalho</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Nome da Praça</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Confeitaria, Linha Quente" className="mt-1" data-testid="praca-name-input" />
            </div>
            
            {members.length > 0 && (
              <>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Líder (pode editar fichas da praça)</label>
                  <select value={leaderId} onChange={(e) => setLeaderId(e.target.value)} className="mt-1 w-full border rounded-md px-3 py-2 text-sm" data-testid="praca-leader-select">
                    <option value="">Sem líder</option>
                    {members.map(m => (
                      <option key={m.id || m._id} value={m.id || m._id}>{m.name || m.email}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Membros</label>
                  <div className="mt-1 space-y-1 max-h-[150px] overflow-y-auto">
                    {members.map(m => {
                      const mid = m.id || m._id;
                      const selected = selectedMembers.includes(mid);
                      return (
                        <button key={mid} onClick={() => toggleMember(mid)}
                          className={`w-full flex items-center gap-2 p-2 rounded-lg text-sm text-left transition-colors ${selected ? "bg-violet-50 border border-violet-200" : "bg-zinc-50 hover:bg-zinc-100 border border-transparent"}`}>
                          <div className={`w-4 h-4 rounded border flex items-center justify-center ${selected ? "bg-violet-600 border-violet-600" : "border-zinc-300"}`}>
                            {selected && <span className="text-white text-[10px]">✓</span>}
                          </div>
                          <span>{m.name || m.email}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
            
            {members.length === 0 && (
              <p className="text-xs text-zinc-400 bg-zinc-50 rounded-lg p-3">
                Adicione membros à equipe primeiro (Central de Módulos → Multi-usuário) para atribuir líderes e membros às praças.
              </p>
            )}
            
            <Button onClick={handleSave} className="w-full bg-zinc-950 text-white hover:bg-zinc-800" data-testid="save-praca-btn">
              {editingPraca ? "Atualizar Praça" : "Criar Praça"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
